/**
 * HamburgerButton component for toggling mobile navigation
 * Provides an animated menu icon that transforms between hamburger and X states
 */
import React from 'react';
import styled from 'styled-components';

/**
 * Props interface for the HamburgerButton component
 */
interface HamburgerButtonProps {
  /** Whether the menu is currently open */
  isOpen: boolean;
  /** Click handler for toggling the menu state */
  onClick: () => void;
}

/**
 * Styled button container that's only visible on mobile
 * Changes background color based on open/closed state
 */
const Button = styled.button<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  background: ${({ isOpen, theme }) => (isOpen ? '#ffffffb5' : theme.text.light + '22')};
  border-radius: 8px;
  cursor: pointer;
  z-index: 300;
  padding: 0;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    background: ${({ isOpen, theme }) => (isOpen ? '#ffffffcc' : theme.text.light + '33')};
  }
`;

/**
 * Animated hamburger icon component
 * Transforms between three bars and an X based on open/closed state
 */
const HamburgerIcon = styled.div<{ isOpen: boolean }>`
  width: 20px;
  height: 16px;
  position: relative;
  transform: rotate(0deg);
  transition: 0.5s ease-in-out;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${({ theme }) => theme.text.light};
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: 0.25s ease-in-out;

    &:nth-child(1) {
      top: ${({ isOpen }) => (isOpen ? '7px' : '0px')};
      transform: ${({ isOpen }) => (isOpen ? 'rotate(135deg)' : 'rotate(0)')};
    }

    &:nth-child(2) {
      top: 7px;
      opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
      left: ${({ isOpen }) => (isOpen ? '-60px' : '0')};
    }

    &:nth-child(3) {
      top: ${({ isOpen }) => (isOpen ? '7px' : '14px')};
      transform: ${({ isOpen }) => (isOpen ? 'rotate(-135deg)' : 'rotate(0)')};
    }
  }
`;

/**
 * HamburgerButton component for mobile navigation
 * Shows an animated burger menu that transforms to an X when open
 *
 * @param isOpen - Whether the menu is currently open
 * @param onClick - Click handler for toggling the menu
 * @returns Animated hamburger button component
 */
const HamburgerButton: React.FC<HamburgerButtonProps> = ({ isOpen, onClick }) => {
  return (
    <Button onClick={onClick} aria-label="Toggle menu" isOpen={isOpen}>
      <HamburgerIcon isOpen={isOpen}>
        <span></span>
        <span></span>
        <span></span>
      </HamburgerIcon>
    </Button>
  );
};

export default HamburgerButton;
